import { RootState, store } from 'redux/store';
import { useEffect, useRef, useState } from 'react';
import useToggle from 'hooks/useToggle';
import { Badge, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { getStores } from 'redux/slices/Stores';
import { StoresPopup } from 'popups/Stores/Stores';
import { getOwners } from 'redux/slices/Owners';
import { getRowIds } from 'helpers/data/dataHandling';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Title, Table, FormInput, Spinner, Modal } from 'components';
import { cleanStoreGroupDetails, getStoreGroupsById, updateField, setStoreGrupStores, updateStoreGroup, getStoreGroupStoreTable, createStoreGroup, getStoreGroupsStoresById, updateStoreGroupStores, deleteStoresByGroup, updateStoreGroupField } from 'redux/slices/StoreGroups';
import { StoreGroup } from 'models/dbModels/storeGroups/StoreGroup';
import { StoreExtended } from 'models/dbModels/stores/Store';

export function NewStoreGroup() {
	const form = useRef(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { storeGroupId } = useParams();
	const [show, setShow] = useState(false);
	const [isValidated, setValidated] = useToggle();
	const [selectedStores, setSelectedStores] = useState<any[]>([]);
	const stores = useAppSelector((state: RootState) => state.stores);
	const owners = useAppSelector((state: RootState) => state.owners);
	const [isNewOwner, setIsNewStoreGroup] = useState<boolean>(true);
	const storeGroup = useAppSelector((state: RootState) => state.storeGroups);
	const [showPrompt, setShowPrompt] = useState<boolean>(false);

	const columns = [
		{
			Header: 'Name',
			accessor: 'name',
			defaultCanSort: true,
			width: 800,
		},
		{
			Header: "Site ID",
			accessor: "id",
			defaultCanSort: true,
			width: 200,
		},
		{
			Header: 'Status',
			accessor: 'connected',
			width: 200,
			defaultCanSort: true,
			Cell: (row: any) => {
				return (
					<>
						{row.cell.row.original.connected === false && <Badge className="me-1 badge-outline-danger">Offline</Badge>}
						{row.cell.row.original.connected === true && <Badge className="me-1 badge-outline-primary">Online</Badge>}
					</>
				);
			}
		},
		{
			Header: "Running Version",
			accessor: "lastRunningVersion",
			defaultCanSort: true,
			width: 200,
		}
	];

	useEffect(() => {
		dispatch(cleanStoreGroupDetails());
		if (storeGroupId) {
			const storeGroupIdNumber = parseInt(storeGroupId);
			dispatch(getStoreGroupsById(storeGroupIdNumber));
			dispatch(getStoreGroupStoreTable(storeGroupIdNumber));
			dispatch(getStoreGroupsStoresById([storeGroupIdNumber]));
			getFullStoresList();
			setIsNewStoreGroup(false);
		} else {
			setIsNewStoreGroup(true);
			dispatch(updateField({ key: 'storesTable', value: [] }));
		}
		dispatch(getOwners({}));
	}, [storeGroupId]);

	useEffect(() => {
		if (storeGroup.stores.length > 0 && storeGroup.storegroup.id.toString() == storeGroupId && storeGroupId != null) {
			let storesTable: StoreExtended[] = [];
			dispatch(getStores({ids: [...storeGroup.stores]})).then((res) => {
				if (res.data) {
					res.data.forEach((store) => {
						if (storeGroup.stores.includes(store.id)) storesTable.push(store);
					});
					dispatch(updateField({ key: 'storesTable', value: storesTable }));
				}
				dispatch(updateField({ key: 'loading', value: false }));
			});
		}
	}, [storeGroup.stores]);

	function getFullStoresList() {
		dispatch(getStores({})).then((res) => {
			if (res.data) dispatch(updateField({ key: 'fullStoresList', value: res.data }));
		});
	}

	function handleDeleteRows() {
		// @ts-ignore
		let newRows: any[] = [...storeGroup.stores];
		for (let index = 0; index < selectedStores.length; index++) {
			const element: any = selectedStores[index];
			if (newRows.indexOf(element) >= 0) newRows.splice(newRows.indexOf(element), 1);
		}
		dispatch(setStoreGrupStores(newRows));
		let newTable: any = [];
		storeGroup.storesTable.forEach((element: any, index: number) => {
			if (!selectedStores.includes(element.id)) newTable.push(element);
		});
		dispatch(updateField({ key: 'storesTable', value: newTable }));
		if (newRows.length === 0) dispatch(deleteStoresByGroup([storeGroup.storegroup.id]));
		setSelectedStores([]);
	}

	function handleSubmit() {
		dispatch(updateField({ key: 'loading', value: true }));
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			let payload: StoreGroup = {...storeGroup.storegroup};

			if (isNewOwner) {
				payload.id = 0;
				dispatch(createStoreGroup(payload)).then((res) => {
					//Success
					if (res && res.data) {

						let id = res.data[0];
						navigate(`/support/storegroup/${id}`);
					} 
					dispatch(updateField({ key: 'loading', value: false }));
				});
			} else {

				dispatch(updateStoreGroup(payload)).then((res) => {
					//Success
					if (res && res.data) {
						let storeIds: any[] = [];
						let payload: any[] = [];
						// Preventing the same store to be added twice
						storeGroup.stores.forEach(store => {
							if (!storeIds.includes(store)) {
								storeIds.push(store);
								payload.push({ storeId: store, storeGroupId: storeGroup.storegroup.id });
							}
						});
						dispatch(updateStoreGroupStores(payload)).then((res) => {
							//Success
							if (res && res.data) {
								navigate('/support/storegroups');
							}
							dispatch(updateField({ key: 'loading', value: false }));
						})
					} else {
						dispatch(updateField({ key: 'loading', value: false }));
					}
				});
			}

		} else {
			dispatch(updateField({ key: 'loading', value: false }));
		}
		setValidated();
	}

	function storesPopupSave() {
		let selectedStores: any[] = [];
		let deselectedStores: any[] = [];
		let storesList: any[] = [...storeGroup.stores];
		if (storeGroup.fullStoresList.length < 0) getFullStoresList();
		if (stores.selectAllFilter && stores.keyword == "") {
			deselectedStores = getRowIds(stores.deselectedRows);
			storeGroup.fullStoresList.forEach((store: any) => {
				if (!deselectedStores.includes(store.id)) selectedStores.push(store.id);
			});
		} else {
			selectedStores = getRowIds(stores.selectedRows);
		}
		// checks for duplicates in the stores list
		selectedStores.forEach((store: any) => {
			if (!storeGroup.stores.includes(store as never)) storesList.push(store);
		});
		dispatch(updateField({ key: 'stores', value: storesList }));
		// dispatch(updateStoreGroupField({ key: 'storesTable', value: storesList }));
		setShow(false);
	}

	return (
		<>
			<Title pageTitle={isNewOwner ? "New Store Group" : "Update Store Group"} parent="Admin" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Form validated={isValidated} ref={form}>
									<Row>
										<h4 className="mt-2">Store Group Details</h4>
										<p className="text-muted">Fill the form below create a new store group.</p>
										<Col md={4}>
											<FormInput
												label="Name"
												type="text"
												name="account-given-name"
												placeholder="Enter name"
												containerClass={'mb-3'}
												required
												value={storeGroup.storegroup.name}
												onChange={(e) => dispatch(updateStoreGroupField({ key: "name", value: e.target.value }))}
											/>
										</Col>
										<Col md={4}>
											<Form.Group>
												<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Owner</Form.Label>
												<Form.Select className="custom-select"
													required
													value={storeGroup.storegroup.ownerId}
													onChange={(e) => dispatch(updateStoreGroupField({ key: "ownerId", value: parseInt(e.target.value) }))}
												>
													<option hidden value={0}>Choose an owner</option>
													{owners.table.map((store: any, index: any) => {
														return <option key={index} value={store.id.toString()}>{store.name}</option>
													})}
												</Form.Select>
											</Form.Group>
										</Col>
										<Col md={4}>
											<FormInput
												label="Comments"
												type="text"
												name="storegroup-comment"
												placeholder="Enter comments"
												containerClass={'mb-3'}
												value={storeGroup.storegroup.comments || ""}
												onChange={(e) => dispatch(updateStoreGroupField({ key: "comments", value: e.target.value }))}
											/>
										</Col>
									</Row>
									{storeGroupId && <>
										<Row>
											<Col md={12}>
												<StoresPopup show={show} onClose={() => setShow(false)} onSave={storesPopupSave} adminMode={true} />
												<Table
													pageSize={15}
													isSortable={true}
													canDelete={true}
													pagination={true}
													isSelectable={true}
													isSearchable={true}
													searchColumns={['Name', 'Site ID']}
													columns={columns}
													loading={storeGroup.loading}
													data={storeGroup.storesTable}
													removeRows={handleDeleteRows}
													addNewRow={() => setShow(true)}
													reloadTableRows={() => dispatch(getStoreGroupStoreTable(parseInt(storeGroupId)))}
													selectedRows={(rows) => {
														const selected = storeGroup.storesTable.filter((i: any, index: number) => rows.includes(index)).map((i: any) => i.id);
														setSelectedStores(selected);
													}}
												/>
											</Col>
										</Row>
									</>}
									<Row className='mt-3'>
										<Col sm={6}>
											<Link to="/support/storegroups" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to All Store Groups {' '}
											</Link>
										</Col>
										<Col sm={6} className="d-flex flex-row-reverse">
											<Button variant="primary" className="btn cta-button" disabled={storeGroup.loading} onClick={() => setShowPrompt(true)}>
												{storeGroup.loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
												{storeGroup.loading ? <>Saving...</> : <>Save</>}
											</Button>
										</Col>
									</Row>
								</Form>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Modal
				title="Confirm - Save Store Group"
				show={showPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowPrompt(false)}
				handleSubmit={() => {
					handleSubmit();
					setShowPrompt(false);
				}}
				description={
					<>
						<p>Data breaches can occur with the incorrect selection of stores/store groups.</p>
						<p>Please double-check your selection.</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>
		</>
	);
}